// Shared layered-background fragment for the UFC fighter panel.
// Consumed by both PickemMode FighterCorner (interactive button) and
// App.jsx UFCFighterSide / UFCMiniFighterSide (display divs).
//
// The CONSUMER provides:
//   • The outer container (button or div) with
//     position:relative + overflow:hidden + cornerTintBg() background
//   • The content layered on top (name block, picked badge, flag chip)
//     at zIndex >= 4
//
// This component renders the 4 absolute-positioned background layers
// as a Fragment. Same z-order as the PickemMode reference:
//
//   z:0 — flag image (omitted when flagUrl is null/falsy)
//   z:1 — fighter silhouette (mix-blend:screen, bolden filter, translateX-centered)
//   z:2 — country-tinted radial glow (mix-blend:screen)
//   z:3 — bottom scrim (linear gradient to dark for name contrast)
//
// EXACT VALUES from the post-bolden-pass PickemMode FighterCorner —
// any tuning happens HERE, not at the call sites, so visual changes
// propagate to all consumers at once.

import { UFC_CORNER_RED, buildCountryGlow } from '../lib/ufcFighterPanel'

export default function FighterPanelLayers({
  cornerColor,    // UFC_CORNER_RED or UFC_CORNER_BLUE — controls silhouette translateX direction
  silhouetteUrl,  // /ufc/fighter-red.webp or /ufc/fighter-blue.webp
  flagUrl,        // ESPN flag PNG url, or null to omit the flag layer (used by mini cards)
  country,        // ESPN flag.alt country string (drives glow palette); fallback to corner-color glow
  side,           // 'left' (red corner / away) or 'right' (blue corner / home) — drives glow position
  flagBlurPx = 6, // blur radius for the flag layer (default 6px tuned for featured
                  // cards ~280-400px tall). Mini cards ~120px tall pass a smaller
                  // value because a fixed 6px is 5-6% of mini-card dimension and
                  // smears the small ESPN flag source into a color field. Pass
                  // ~2 for mini sizes to keep country identity recognizable.
  glowOpacity = 1, // multiplier on the country glow (z:2) layer. Default 1
                   // tuned for featured cards where the silhouette fills enough
                   // space that the screen-blended glow only halos around it.
                   // Mini cards pass ~0.45 because the glow's 85x75 radial fills
                   // most of the visible area at small sizes and screen-blends
                   // the flag pattern away. Pass lower to let the flag pattern
                   // show through clearly.
}) {
  return (
    <>
      {/* z:0 — flag layer. Opacity 0.78 + blur(6px) — the bands/stars
          stay recognizable as country identity but soften enough that
          the silhouette becomes the clear focal point (per mockup
          2026-06-11). transform:scale(1.08) over-fills the container
          so the blur's soft edges land outside the clip area instead
          of vignetting at the card border. Omitted entirely on mini
          cards (caller passes flagUrl=null) where the detail becomes
          pixel noise. */}
      {flagUrl && (
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: `url('${flagUrl}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
          opacity: 0.78,
          filter: `blur(${flagBlurPx}px)`,
          transform: 'scale(1.08)',
          pointerEvents: 'none',
          zIndex: 0,
        }}/>
      )}

      {/* z:1 — silhouette layer. mix-blend:screen drops the source's
          solid black background so the flag (or the corner tint when
          flagUrl is null) shows through everywhere except the
          red/blue-lit fighter. Bolden values + translateX centering
          per the PickemMode reference. The translateX percentages
          are invariant to card width — they describe FIGURE-OFFSET
          relative to card width, which scales with backgroundSize:120%
          identically at any card size (verified: source figure center
          at 37.3% red / 71.8% blue; with 120% size the overflow
          contribution is 0.10W on each side regardless of W). */}
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: `url('${silhouetteUrl}')`,
        backgroundSize: '120%',
        backgroundPosition: 'center bottom',
        backgroundRepeat: 'no-repeat',
        mixBlendMode: 'screen',
        opacity: 1.0,
        transform: cornerColor === UFC_CORNER_RED
          ? 'translateX(15%)'
          : 'translateX(-26%)',
        filter: 'brightness(1.75) contrast(1.35) saturate(1.30)',
        pointerEvents: 'none',
        zIndex: 1,
      }}/>

      {/* z:2 — country-tinted glow. Multi-stop radial when the country
          has a flag palette; single-color cornerColor radial otherwise.
          mix-blend:screen so the lit fighter pixels brighten through.
          opacity multiplier lets mini-card consumers dim the glow so
          the flag pattern stays visible underneath. */}
      <div style={{
        position: 'absolute', inset: 0,
        background: buildCountryGlow({ country, side, cornerColor }),
        mixBlendMode: 'screen',
        opacity: glowOpacity,
        pointerEvents: 'none',
        zIndex: 2,
      }}/>

      {/* z:3 — bottom scrim. Holds name/record contrast over the
          silhouette without darkening the upper torso area. */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(to bottom, transparent 38%, rgba(13,16,22,0.7) 68%, rgba(13,16,22,0.95) 100%)',
        pointerEvents: 'none',
        zIndex: 3,
      }}/>
    </>
  )
}
